<script setup lang="ts">
import {
  computed,
  defineOgImageComponent,
  onMounted,
  ref,
  useUser,
  useCreators,
  useDialog,
  useHead,
  useMetrics,
  useRoute,
  useToast,
  callOnce,
  watch,
  useMaps,
  useHeaderStyle,
} from "#imports";
import { uniqBy } from "lodash-es";
import MPShare from "~/components/dialogs/MPShare.vue";
import MPPricing from "~/components/dialogs/MPPricing.vue";
import MPButton from "~/components/forms/MPButton.vue";
import MPFollow from "~/components/widgets/MPFollow.vue";
import MPImg from "~/components/widgets/MPImg.vue";
import MPCategoryList from "~/components/widgets/MPCategoryList.vue";
import type { UserTier } from "~/stores/user";

useHeaderStyle("opaque");

const { openDialog } = useDialog();
const { showError } = useToast();
const { trackEvent } = useMetrics();

const route = useRoute();
const creatorStore = useCreators();
const userStore = useUser();
const mapStore = useMaps();

const loadData = async () => {
  if (typeof route.params.handle !== "string") {
    return;
  }
  const creator = await creatorStore.fetchCreatorByHandle(route.params.handle);
  await mapStore.fetchByIds(creator.value!.mapIds);
};

await callOnce(loadData, { mode: "navigation" });
watch(() => route.params.handle, loadData);

const data = computed(() => {
  const creator = creatorStore.creatorByHandle!;
  const maps = mapStore.getByIds(creator?.mapIds ?? []).value;
  const categories = uniqBy(
    maps?.flatMap((map) => map.categories),
    "id",
  );

  return {
    ...creator,
    isSupporter: userStore.isSupporter(creator?.id).value,
    maps,
    categories,
  };
});

// // Open Social Links in New Tab
const openInNewTab = (url: string) => {
  window.open(url, "_blank", "noopener,noreferrer");
};

useHead(() => ({
  title: `${data.value.display_name} - @${data.value.mappr_handle}`,
  meta: [
    {
      name: "description",
      content: data.value.bio_text,
    },
  ],
}));

defineOgImageComponent(
  "Default",
  {
    title: `${data.value.display_name} - @${data.value.mappr_handle}`,
    description: data.value.bio_text,
  },
  {
    url: data.value.profile_image_url ?? "/media/images/placeholder-user.webp",
  },
);

// On Load fix issue where page starts scrolled half way
onMounted(() => {
  window.scrollTo(0, 0);
  trackEvent("viewed_profile", {
    creator_handle: data.value.mappr_handle,
    creator_id: data.value.id,
  });
});

const openShareDialog = () => {
  try {
    openDialog(MPShare, {
      id: data.value.id,
      version: "creator",
    });
  } catch (e) {
    showError("Error opening share dialog", e);
  }
};

const pricingTier = ref<UserTier>("premium");
const openPricingDialog = (tier: UserTier) => {
  if (!creatorStore.creatorByHandle) {
    return;
  }
  pricingTier.value = tier;
  openDialog(MPPricing, {
    creatorId: data.value.id,
    showSupportTier: !!creatorStore.creatorByHandle?.stripe_account_id,
    hideSubscriberTier: true,
  });
  trackEvent(`${tier}_intent`, {
    creator_id: creatorStore.creatorByHandle?.id,
    creator_handle: creatorStore.creatorByHandle?.mappr_handle,
  });
};
onMounted(() => {
  if (import.meta.client && window) {
    window.history.scrollRestoration = "auto";
  }
});
</script>

<template>
  <main v-if="creatorStore.creatorByHandle">
    <div class="creator-header">
      <div class="creator-image">
        <MPImg
          :width="320"
          :height="320"
          :src="data.profile_image_url ?? '/media/images/placeholder-user.webp'"
          alt="Creator Profile Image"
        />
      </div>
      <div v-if="data.display_name" class="creator-display-name">
        {{ data.display_name }}
      </div>
      <h1 class="creator-handle">@{{ data.mappr_handle }}</h1>
      <MPCategoryList
        class="creator-categories"
        :categories="data.categories"
      />
      <div class="creator-bio typeface-summary">
        {{ data.bio_text }}
      </div>
      <div class="creator-actions">
        <MPButton
          v-show="!data.isSupporter && data.stripe_account_id"
          label="Support"
          class="mobile-support-button"
          version="primary"
          @click="openPricingDialog('supporter')"
        />
        <MPFollow :creatorId="data.id" />
        <MPButton
          icon="ri-share-line"
          version="secondary"
          link
          hideLabel
          backgroundColor="var(--color-slate-200)"
          @click="openShareDialog()"
        />
      </div>
    </div>
    <div class="creator-content">
      <NuxtPage />
    </div>
    <template
      v-if="
        data.tiktok_id ||
        data.facebook_id ||
        data.instagram_id ||
        data.twitter_id ||
        data.youtube_id ||
        data.substack_id
      "
    >
      <h4 class="section-title">Find @{{ data.mappr_handle }} online</h4>
      <div class="creator-links">
        <MPButton
          v-if="data.tiktok_id"
          icon="ri-tiktok-line"
          label="tiktok"
          hideLabel
          version="secondary"
          @click="openInNewTab(`https://www.tiktok.com/@${data.tiktok_id}`)"
        />
        <MPButton
          v-if="data.facebook_id"
          icon="ri-facebook-fill"
          label="facebook"
          hideLabel
          version="secondary"
          @click="openInNewTab(`https://facebook.com/@${data.facebook_id}`)"
        />
        <MPButton
          v-if="data.instagram_id"
          icon="ri-instagram-line"
          label="instagram"
          hideLabel
          version="secondary"
          @click="openInNewTab(`https://instagram.com/${data.instagram_id}`)"
        />
        <MPButton
          v-if="data.twitter_id"
          icon="ri-twitter-x-line"
          label="twitter/x"
          hideLabel
          version="secondary"
          @click="openInNewTab(`https://twitter.com/${data.twitter_id}`)"
        />
        <MPButton
          v-if="data.youtube_id"
          icon="ri-youtube-fill"
          label="youtube"
          hideLabel
          version="secondary"
          @click="openInNewTab(`https://youtube.com/@${data.youtube_id}`)"
        />
        <MPButton
          v-if="data.substack_id"
          icon="ri-links-line"
          label="substack"
          hideLabel
          version="secondary"
          @click="openInNewTab(`https://substack.com/@${data.substack_id}`)"
        />
      </div>
    </template>
  </main>
</template>

<style lang="postcss" scoped>
@import "@/assets/css/layouts.pcss";
@import "@/assets/css/breakpoints.pcss";

main {
  position: relative;
  top: -5rem;
  text-align: center;
  @mixin screen md {
    top: 0;
  }
}

.creator-header {
  background-color: var(--color-slate-50);
  padding: 4rem 1rem 2rem;
  align-items: center;
  display: grid;
  @mixin screen md {
    top: 0;
    padding-top: 8rem;
  }
}

.creator-image {
  width: 20rem;
  height: 20rem;
  margin: 0 auto 2rem;
  border-radius: 50%;
  overflow: hidden;
  :deep(img) {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.creator-display-name {
  color: var(--color-rose-400);
}

.creator-handle {
  margin: 0.5rem 0 0.75rem;
}

.creator-bio {
  max-width: 40rem;
  margin: 0 auto;
  line-height: 1.5;
}

.creator-links {
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin: 1rem 0 4rem;
}

.creator-actions {
  display: flex;
  justify-content: center;
  gap: 0.75rem;
  margin-top: 1rem;
}

.creator-content {
  @mixin layout-wrapper-narrow;
  padding: 2rem;
  text-align: left;
}

.creator-categories {
  display: none;
  margin: 0 auto;
  @mixin screen md {
    display: inline-flex;
    margin: 1rem auto 3rem;
  }
}

.section-title {
  margin: 2rem auto;
  color: var(--color-slate-700);
  text-align: center;
}
</style>
